import React, { useState, useEffect, useContext, useRef } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useMediaQuery } from "react-responsive";
import { DirectionContext } from "../App";
import Cookies from "js-cookie";
import API_ENDPOINTS from "../config/api";

const LawyerVerification = () => {
  const isDesktop = useMediaQuery({ minWidth: 768 });
  const { navigateTo } = useContext(DirectionContext);
  const [selectedOption, setSelectedOption] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [timer, setTimer] = useState(600); // 10분 (600초)
  const [resendTimer, setResendTimer] = useState(0); // 재전송 타이머
  const [isVerificationRequested, setIsVerificationRequested] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [verificationFailed, setVerificationFailed] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showRateLimitModal, setShowRateLimitModal] = useState(false); // 요청 제한 모달 상태 추가
  const [verificationToken, setVerificationToken] = useState(""); // 인증 토큰 저장
  const [modalType, setModalType] = useState(""); // 모달 타입 상태 추가
  const verificationInputRef = useRef(null);
  const buttonsContainerRef = useRef(null); // 버튼 컨테이너에 대한 ref 추가
  const [representativeName, setRepresentativeName] = useState("");

  // 애니메이션 변수
  const pageVariants = {
    initial: {
      opacity: 0,
    },
    in: {
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
    out: {
      opacity: 0,
      transition: {
        duration: 0.3,
      },
    },
  };

  const contentVariants = {
    initial: {
      opacity: 0,
      y: 20,
    },
    in: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: 0.2,
        staggerChildren: 0.1,
      },
    },
  };

  // 타이머 기능
  useEffect(() => {
    let interval;
    if (isVerificationRequested && timer > 0) {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isVerificationRequested, timer]);

  // 재전송 타이머 기능
  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  // 타이머 포맷 (분:초)
  const formatTime = () => {
    const minutes = Math.floor(timer / 60);
    const seconds = timer % 60;
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  // 인증번호 요청 처리
  const handleRequestVerification = async () => {
    if (phoneNumber.length === 11) {
      setIsVerificationRequested(true);
      setVerificationCode(""); // 인증번호 초기화
      setTimer(600); // 타이머 초기화
      setResendTimer(60); // 재전송 타이머 15초로 설정
      setVerificationFailed(false); // 실패 메시지 초기화

      try {
        // API 호출로 인증번호 요청
        const response = await fetch(
          `${API_ENDPOINTS.BASE_URL}/auth/send-otp`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              phoneNumber: phoneNumber,
            }),
          }
        );

        // 429 상태 코드 체크 (요청 제한 초과)
        if (response.status === 429) {
          setIsVerificationRequested(false);
          setModalType("rate-limit");
          setShowRateLimitModal(true);
          throw new Error(
            "요청 제한을 초과했습니다. 잠시 후 다시 시도해주세요."
          );
        }

        if (response.status !== 201) {
          throw new Error("인증번호 전송에 실패했습니다.");
        }

        // 인증번호 입력 필드에 포커스
        setTimeout(() => {
          if (verificationInputRef.current) {
            verificationInputRef.current.focus();
          }
        }, 100);
      } catch (error) {
        // 요청 제한 초과가 아닌 경우에만 일반 실패 처리
        if (!error.message.includes("요청 제한을 초과")) {
          setVerificationFailed(true);
          setIsVerificationRequested(false);
        }
      }
    }
  };

  // 인증번호 재전송 처리
  const handleResendVerification = async () => {
    setTimer(600); // 타이머 초기화
    setResendTimer(60); // 재전송 타이머 15초로 설정
    setVerificationCode(""); // 인증번호 초기화
    setVerificationFailed(false); // 실패 메시지 초기화

    try {
      // API 호출로 인증번호 재요청
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/auth/send-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          phoneNumber: phoneNumber,
        }),
      });

      // 429 상태 코드 체크 (요청 제한 초과)
      if (response.status === 429) {
        setModalType("rate-limit");
        setShowRateLimitModal(true);
        throw new Error("요청 제한을 초과했습니다. 잠시 후 다시 시도해주세요.");
      }

      if (response.status !== 201) {
        throw new Error("인증번호 재전송에 실패했습니다.");
      }

      // 인증번호 입력 필드에 포커스
      setTimeout(() => {
        if (verificationInputRef.current) {
          verificationInputRef.current.focus();
        }
      }, 100);
    } catch (error) {
      // 요청 제한 초과가 아닌 경우에만 일반 실패 처리
      if (!error.message.includes("요청 제한을 초과")) {
        setVerificationFailed(true);
      }
    }
  };

  // 인증 검증 처리
  const handleVerify = async () => {
    if (verificationCode.length === 6) {
      try {
        // 인증번호 검증 API 호출
        const response = await fetch(
          `${API_ENDPOINTS.BASE_URL}/auth/verify-otp`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              phoneNumber: phoneNumber,
              code: verificationCode,
            }),
          }
        );

        if (response.status !== 201) {
          throw new Error("인증에 실패했습니다.");
        }

        const data = await response.json();

        if (data.verified) {
          // 토큰 검증 API 호출
          const tokenResponse = await fetch(
            `${API_ENDPOINTS.BASE_URL}/auth/verify-token`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                token: data.token,
              }),
            }
          );

          if (tokenResponse.status !== 201) {
            throw new Error("토큰 검증에 실패했습니다.");
          }

          // 인증 성공 처리
          setIsVerified(true);
          setVerificationFailed(false);
          setVerificationToken(data.token);

          // 토큰과 전화번호를 쿠키에 저장
          Cookies.set(
            API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN,
            data.token,
            {
              expires: API_ENDPOINTS.COOKIE_CONFIG.EXPIRES.ACCESS_TOKEN,
              ...API_ENDPOINTS.COOKIE_CONFIG.OPTIONS,
            }
          );

          Cookies.set("verifiedPhoneNumber", phoneNumber, {
            expires: 7,
            ...API_ENDPOINTS.COOKIE_CONFIG.OPTIONS,
          });

          // 이미 신청 완료한 집단소송이 있는지 또는 coApplicant 상태 확인
          if (
            data.application &&
            (data.application.status === "PAYMENT_COMPLETED" ||
              data.application.lawsuitId === 1)
          ) {
            // 이미 집단소송 신청을 완료한 사용자
            // setModalType("already-completed");
            // setShowModal(true);
            // setSelectedOption("already-completed");
            if (data.coApplicant.representativeName) {
              setRepresentativeName(data.coApplicant.representativeName);
            }

            if (data.coApplicant.status === "PENDING") {
              // 서명 대기 중
              setModalType("signature-pending");
              setShowModal(true);
            } else if (data.coApplicant.status === "VERIFIED") {
              // 서명 완료됨
              setModalType("signature-verified");
              setShowModal(true);
            }
          } else if (data.coApplicant) {
            // 대표신청자 이름 저장
            if (data.coApplicant.representativeName) {
              setRepresentativeName(data.coApplicant.representativeName);
            }

            if (data.coApplicant.status === "PENDING") {
              // 서명 대기 중
              setModalType("signature-pending");
              setShowModal(true);
            } else if (data.coApplicant.status === "VERIFIED") {
              // 서명 완료됨
              setModalType("signature-verified");
              setShowModal(true);
            }
          }

          // 인증 완료 후 버튼 컨테이너로 스크롤
          setTimeout(() => {
            buttonsContainerRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "end",
            });
          }, 300);
        } else {
          throw new Error("인증에 실패했습니다.");
        }
      } catch (error) {
        setIsVerified(false);
        setVerificationFailed(true);
      }
    }
  };

  // 다음 단계로 이동
  const handleNext = async () => {
    try {
      // 토큰 가져오기
      const token = Cookies.get(API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN);

      if (!token) {
        throw new Error("인증 토큰이 없습니다. 다시 로그인해주세요.");
      }

      // 사용자 정보 먼저 가져오기
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/clients/me`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("사용자 정보를 가져오는데 실패했습니다.");
      }

      const data = await response.json();

      // 이미 신청 완료한 집단소송이 있는지 확인
      const hasCompletedApplication =
        data.applications &&
        data.applications.some(
          (app) => app.status === "PAYMENT_COMPLETED" || app.lawsuitId === 1
        );

      if (hasCompletedApplication) {
        // 이미 집단소송 신청을 완료한 사용자
        // setShowModal(true);
        // setSelectedOption("already-completed");
        const telecomProviderResponse = await fetch(
          `${API_ENDPOINTS.BASE_URL}/clients/me`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              phoneNumber: phoneNumber,
              telecom: selectedOption === "skt" ? "SKT" : "OTHER",
              ...(Cookies.get("referralSource") && Cookies.get("referralDetail")
                ? {
                    referralSource: Cookies.get("referralSource"),
                    referralDetail: Cookies.get("referralDetail"),
                  }
                : {}),
            }),
          }
        );

        if (!telecomProviderResponse.ok) {
          throw new Error("통신사 정보 업데이트에 실패했습니다.");
        }

        // 다음 단계로 진행 - /user-data 대신 /application-type으로 이동
        navigateTo("/application-type");
      } else {
        // 신청 완료하지 않은 사용자만 통신사 정보 업데이트
        const telecomProviderResponse = await fetch(
          `${API_ENDPOINTS.BASE_URL}/clients/me`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              phoneNumber: phoneNumber,
              telecom: selectedOption === "skt" ? "SKT" : "OTHER",
              ...(Cookies.get("referralSource") && Cookies.get("referralDetail")
                ? {
                    referralSource: Cookies.get("referralSource"),
                    referralDetail: Cookies.get("referralDetail"),
                  }
                : {}),
            }),
          }
        );

        if (!telecomProviderResponse.ok) {
          throw new Error("통신사 정보 업데이트에 실패했습니다.");
        }

        // 다음 단계로 진행 - /user-data 대신 /application-type으로 이동
        navigateTo("/application-type");
      }
    } catch (error) {
      // 오류 발생 시 기본 동작으로 다음 페이지로 이동
      navigateTo("/application-type");
    }
  };

  // 모달 확인 버튼
  const handleModalConfirm = () => {
    setShowModal(false);

    // 이미 완료한 사용자인 경우 select 페이지로 이동
    if (modalType === "already-completed") {
      navigateTo("/select");
    }
    // 다른 타입의 모달은 각각 처리
    else if (modalType === "signature-pending") {
      // 서명 대기 중일 경우 적절한 페이지로 이동
      navigateTo("/select");
    } else if (modalType === "signature-verified") {
      // 서명 완료된 경우 적절한 페이지로 이동
      navigateTo("/select");
    }
    // 다른 통신사 사용자인 경우 모달만 닫고 상태는 유지
  };

  // 요청 제한 모달 닫기
  const handleRateLimitModalClose = () => {
    setShowRateLimitModal(false);
  };

  // 아니오 버튼 클릭 시 모달 표시 (함수 중복 제거)
  // const handleOtherCarrier = () => {
  //   setSelectedOption("other");
  //   setShowModal(true);
  // };

  // 모달이 열리면 body에 no-scroll 클래스 추가
  useEffect(() => {
    if (showModal) {
      // 모달이 열렸을 때만 스크롤 방지, 그 외에는 스크롤 허용
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      // 컴포넌트 언마운트 시 스크롤 허용 상태로 복원
      document.body.style.overflow = "auto";
    };
  }, [showModal]);

  // 6자리 입력 시 키보드 닫기
  const handleVerificationCodeChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "").slice(0, 6);
    setVerificationCode(value);

    // 6자리 입력 완료 시 자동으로 키보드 닫기
    if (value.length === 6) {
      verificationInputRef.current.blur();
    }
  };

  return (
    <PageBackground>
      <Container
        variants={pageVariants}
        initial="initial"
        animate="in"
        exit="out"
      >
        <Content variants={contentVariants} initial="initial" animate="in">
          <div>
            <Title>1. 통신사 및 번호확인</Title>
          </div>
          <Header>
            <StepIndicator>1 / 4</StepIndicator>
            <SubTitle>가입자 확인 및 번호확인</SubTitle>
            <InfoMessage>
              <InfoIcon>ⓘ</InfoIcon>
              <InfoTextContainer>
                {selectedOption === "skt" || selectedOption === "" ? (
                  <>
                    <InfoTextBold>
                      본 인증은 해당 사고와 관련이 없으며,
                    </InfoTextBold>
                    <InfoTextRegular>
                      SK텔레콤 가입자 확인을 위한 정상적인 절차입니다
                    </InfoTextRegular>
                  </>
                ) : (
                  <>
                    <InfoTextBold2>
                      본 신청은{" "}
                      <HighlightedText>
                        4/18일 이후 해지 및 변경한
                      </HighlightedText>{" "}
                      가입자 대상입니다.
                    </InfoTextBold2>
                  </>
                )}
              </InfoTextContainer>
            </InfoMessage>
          </Header>

          <FormContainer>
            <FormGroup>
              <Label>현재 SKT 통신사를 사용하시나요?</Label>
              <RadioGroup>
                <RadioOption
                  isSelected={selectedOption === "skt"}
                  onClick={() => !isVerified && setSelectedOption("skt")}
                  disabled={isVerified}
                >
                  <RadioLabel>
                    <RadioLabelTitle>예</RadioLabelTitle>
                    <RadioLabelText>
                      SKT 또는 SKT알뜰폰 사용자입니다.
                    </RadioLabelText>
                  </RadioLabel>
                  {/* {isVerified && selectedOption === "skt" && (
                    <VerifiedBadge>인증 완료</VerifiedBadge>
                  )} */}
                  <RadioCircle
                    isSelected={selectedOption === "skt"}
                    disabled={isVerified}
                  >
                    {selectedOption === "skt" && <RadioInner />}
                  </RadioCircle>
                </RadioOption>
                <RadioOption
                  isSelected={selectedOption === "other"}
                  onClick={() => !isVerified && setSelectedOption("other")}
                  disabled={isVerified}
                >
                  <RadioLabel>
                    <RadioLabelTitle>
                      KT, LG로 통신사 변경하였습니다.
                    </RadioLabelTitle>
                    {/* <RadioLabelText>
                      KT,LG 통신사로 변경하였습니다.
                    </RadioLabelText> */}
                  </RadioLabel>
                  {/* {isVerified && selectedOption === "other" && (
                    <VerifiedBadge>인증 완료</VerifiedBadge>
                  )} */}
                  <RadioCircle
                    isSelected={selectedOption === "other"}
                    disabled={isVerified}
                  >
                    {selectedOption === "other" && <RadioInner />}
                  </RadioCircle>
                </RadioOption>
              </RadioGroup>
            </FormGroup>

            {(selectedOption === "skt" || selectedOption === "other") && (
              <FormGroup>
                <Label>휴대폰 번호를 입력해주세요.</Label>
                <PhoneInputContainer>
                  <PhoneInput
                    type="tel"
                    placeholder="-없이 숫자만 입력"
                    value={phoneNumber}
                    onChange={(e) =>
                      setPhoneNumber(
                        e.target.value.replace(/[^0-9]/g, "").slice(0, 11)
                      )
                    }
                  />
                  <VerifyButtonWrapper>
                    {phoneNumber.length === 11 ? (
                      isVerificationRequested ? (
                        resendTimer === 0 && !isVerified ? (
                          <VerifyButton onClick={handleResendVerification}>
                            재전송
                          </VerifyButton>
                        ) : (
                          <VerifyButtonDisabled>재전송</VerifyButtonDisabled>
                        )
                      ) : (
                        <VerifyButton onClick={handleRequestVerification}>
                          인증요청
                        </VerifyButton>
                      )
                    ) : (
                      <VerifyButtonDisabled>인증요청</VerifyButtonDisabled>
                    )}
                  </VerifyButtonWrapper>
                </PhoneInputContainer>
              </FormGroup>
            )}

            {isVerificationRequested &&
              (selectedOption === "skt" || selectedOption === "other") && (
                <FormGroup>
                  <VerificationInputContainer>
                    <VerificationInput
                      type="text"
                      placeholder="인증번호 입력하기"
                      value={verificationCode}
                      onChange={handleVerificationCodeChange}
                      ref={verificationInputRef}
                      inputMode="numeric"
                      pattern="[0-9]*"
                    />
                    <VerifyButtonWrapper>
                      {verificationCode.length === 6 && !isVerified ? (
                        <VerifyButton onClick={handleVerify}>
                          인증하기
                        </VerifyButton>
                      ) : (
                        <VerifyButtonDisabled>인증하기</VerifyButtonDisabled>
                      )}
                    </VerifyButtonWrapper>
                  </VerificationInputContainer>

                  <VerificationHint>
                    인증번호가 오지 않았다면{" "}
                    <ResendHighlight>재전송</ResendHighlight>을 눌러주세요.
                  </VerificationHint>

                  {isVerified && (
                    <VerificationSuccess>
                      인증이 완료되었습니다.
                    </VerificationSuccess>
                  )}

                  {verificationFailed && (
                    <VerificationFail>
                      인증에 실패했습니다. 인증번호를 다시 확인해주세요.
                    </VerificationFail>
                  )}
                </FormGroup>
              )}
          </FormContainer>

          <ButtonsContainer ref={buttonsContainerRef}>
            <ActionButton
              onClick={handleNext}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={
                !selectedOption ||
                phoneNumber.length !== 11 ||
                !isVerificationRequested ||
                !isVerified
              }
            >
              다음으로
            </ActionButton>
          </ButtonsContainer>

          {/* 모달 */}
          {showModal && (
            <ModalOverlay>
              <ModalContainer>
                <ModalContent>
                  {modalType === "already-completed" ? (
                    <ModalText>
                      이미 집단소송 신청을 완료하셨습니다.
                      <br />
                      소송 진행 상황은 메인 페이지에서 확인하실 수 있습니다.
                    </ModalText>
                  ) : modalType === "signature-pending" ? (
                    <ModalText>
                      {representativeName ? `${representativeName}님의 ` : ""}
                      공동소송 신청에 대한 서명이 대기 중입니다.
                    </ModalText>
                  ) : modalType === "signature-verified" ? (
                    <ModalText>
                      {representativeName ? `${representativeName}님의 ` : ""}
                      공동소송 신청에 대한 서명이 완료되었습니다.
                    </ModalText>
                  ) : (
                    <ModalText>
                      <Bold>타 통신사</Bold> 사용자도 신청 가능합니다.
                    </ModalText>
                  )}
                  <ModalButton onClick={handleModalConfirm}>확인</ModalButton>
                </ModalContent>
              </ModalContainer>
            </ModalOverlay>
          )}

          {/* 요청 제한 모달 */}
          {showRateLimitModal && (
            <ModalOverlay>
              <ModalContainer>
                <ModalContent>
                  <ModalTitle>요청 제한 초과</ModalTitle>
                  <ModalText>
                    인증번호 요청이 너무 많습니다.
                    <br />
                    잠시 후 다시 시도해주세요.
                  </ModalText>
                  <ModalButton onClick={handleRateLimitModalClose}>
                    확인
                  </ModalButton>
                </ModalContent>
              </ModalContainer>
            </ModalOverlay>
          )}
        </Content>
      </Container>
    </PageBackground>
  );
};

// 스타일 컴포넌트
const PageBackground = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 0;
  background-color: #f8f8f8;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (min-width: 769px) {
    padding: 40px 20px 60px;
    background-color: #fff;
  }
`;

const Container = styled(motion.div)`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;

  background-color: #f8f8f8;

  /* 모바일 디바이스 */
  @media (max-width: 768px) {
    min-height: auto;
    box-shadow: none;
    padding: 0 0 80px 0;
    max-width: 100%;
    margin: 0;
    border-radius: 0;
  }

  /* 데스크탑 디바이스 */
  @media (min-width: 769px) {
    border-radius: 20px;
    min-height: auto;
    max-height: none;
    max-width: 470px;
    margin: 0 auto;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
    padding: 20px;
  }
`;

const Content = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  width: 100%;
  position: relative;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 30px 20px 40px;
  }

  @media (min-width: 769px) {
    padding: 40px 30px 40px 30px;
  }
`;

const Header = styled.div`
  width: 100%;
  margin-bottom: 20px;
  color: #333;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const StepIndicator = styled.div`
  display: flex;
  width: 54px;
  height: 29px;
  padding: 10px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  border-radius: 8px;
  background: #0075ff;
  color: white;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 15px;
  margin-top: 25px;
  align-self: flex-start;
`;

const Title = styled.h1`
  color: #00285b;
  text-align: center;
  font-family: Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 140%;
  letter-spacing: -0.34px;
  margin: 5px 0;
  padding-top: 10px;
  word-break: keep-all;
`;

const SubTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 20px;
  word-break: keep-all;
  color: #333;
  text-align: left;
  width: 100%;
`;

const InfoMessage = styled.div`
  display: flex;
  align-items: flex-start;
  background-color: rgba(0, 102, 255, 0.1);
  border-radius: 8px;
  padding: 16px;
  width: 100%;
  align-self: flex-start;
`;

const InfoIcon = styled.span`
  color: #0075ff;
  margin-top: 5px;
  font-size: 16px;
  margin-right: 10px;
  font-weight: bold;
`;

const InfoTextContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const InfoTextBold = styled.span`
  color: #4b5563;
  font-family: Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 140%;
`;
const InfoTextBold2 = styled.span`
  color: #4b5563;
  font-family: Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
  margin-top: 5px;
`;

const HighlightedText = styled.span`
  color: #dc4b3f;
  font-weight: 800;
`;

const InfoTextRegular = styled.span`
  color: #4b5563;
  font-family: Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
`;

const FormContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 30px;
  box-sizing: border-box;
`;

const FormGroup = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: 0;
`;

const SubText = styled.p`
  font-size: 16px;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
`;

const Label = styled.label`
  display: block;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #333;
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  box-sizing: border-box;
  padding: 0;
`;

const RadioOption = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-radius: 8px;
  background-color: ${(props) =>
    props.isSelected
      ? props.disabled
        ? "rgba(0, 117, 255, 0.05)"
        : "rgba(0, 117, 255, 0.1)"
      : "white"};
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};
  transition: all 0.2s ease;
  border: 1px solid ${(props) => (props.isSelected ? "#0075FF" : "#e5e5e5")};
  opacity: ${(props) => (props.disabled && !props.isSelected ? 0.6 : 1)};
  width: 100%;
  box-sizing: border-box;

  &:hover {
    background-color: ${(props) =>
      props.disabled ? "inherit" : "rgba(0, 117, 255, 0.05)"};
  }
`;

const RadioCircle = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid ${(props) => (props.isSelected ? "#0075FF" : "#ccc")};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  opacity: ${(props) => (props.disabled ? 0.7 : 1)};
`;

const RadioInner = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: #0075ff;
`;

const RadioLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RadioLabelTitle = styled.div`
  color: #2e3840;
  font-feature-settings: "liga" off, "clig" off;
  font-family: Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
`;

const RadioLabelText = styled.div`
  color: #4b5563;
  font-feature-settings: "liga" off, "clig" off;
  font-family: Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`;

const PhoneInputContainer = styled.div`
  display: flex;
  width: 100%;
  position: relative;
  box-sizing: border-box;
  padding: 0;
`;

const PhoneInput = styled.input`
  flex: 1;
  height: 60px;
  border-radius: 8px;
  border: 1px solid #e5e5e5;
  padding: 0 100px 0 16px;
  font-size: 16px;
  background-color: white;
  width: 100%;
  box-sizing: border-box;

  &::placeholder {
    color: #999;
  }
`;

const VerifyButtonWrapper = styled.div`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
`;

const VerifyButton = styled.button`
  display: flex;
  padding: 8px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 3px;
  background: #f2f9ff;
  color: #0094ef;
  text-align: right;
  font-family: Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 125%;
  border: none;
  cursor: pointer;
  white-space: nowrap;
`;

const VerifyButtonDisabled = styled(VerifyButton)`
  color: #999;
  background: #f5f5f5;
  cursor: default;
`;

const VerificationInputContainer = styled.div`
  display: flex;
  width: 100%;
  position: relative;
  margin-bottom: 8px;
  box-sizing: border-box;
  padding: 0;
`;

const VerificationInput = styled.input`
  flex: 1;
  height: 60px;
  border-radius: 8px;
  border: 1px solid #e5e5e5;
  padding: 0 100px 0 16px;
  font-size: 16px;
  background-color: white;
  width: 100%;
  box-sizing: border-box;

  &::placeholder {
    color: #999;
  }
`;

const VerificationStatus = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  margin-top: 8px;
`;

const VerificationLabel = styled.span`
  flex: 1;
  font-size: 14px;
  color: #333;
`;

const VerificationTimer = styled.span`
  font-size: 14px;
  color: #ff6b6b;
  margin-right: 16px;
`;

const VerifyNowButton = styled.button`
  display: flex;
  padding: 8px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 3px;
  background: #f2f9ff;
  color: #0094ef;
  text-align: right;
  font-family: Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 125%;
  border: none;
  cursor: pointer;
`;

const VerificationSuccess = styled.div`
  width: 100%;
  padding: 10px;
  margin-top: 10px;
  background-color: rgba(0, 200, 83, 0.1);
  color: #00c853;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
`;

const VerificationFail = styled.div`
  width: 100%;
  padding: 10px;
  margin-top: 10px;
  background-color: rgba(255, 76, 76, 0.1);
  color: #ff4c4c;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
`;

const VerificationHint = styled.p`
  font-size: 14px;
  color: #666;
  margin-top: 8px;
`;

const ResendText = styled.span`
  color: #0094ef;
  cursor: pointer;
  font-weight: 600;
`;

const ResendTextDisabled = styled.span`
  color: #999;
  cursor: default;
  font-weight: 600;
`;

const ButtonsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 10px;
  padding: 0 0px;
  background-color: #fff;

  @media (min-width: 769px) {
    padding: 0;
    background-color: transparent;
  }
`;

const ActionButton = styled(motion.button)`
  display: flex;
  width: 100%;
  max-width: 400px;
  height: 56px;
  padding: 0px 14px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  border-radius: 8px;
  background: #0075ff;
  color: white;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};

  @media (min-width: 769px) {
    max-width: 430px;
    margin: 0 auto;
  }
`;

// 모달 관련 스타일
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  touch-action: none;
`;

const ModalContainer = styled.div`
  width: 90%;
  max-width: 355px;
  background-color: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
`;

const ModalContent = styled.div`
  padding: 30px 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const ModalText = styled.p`
  font-size: 16px;
  color: #333;
  text-align: left;
  margin-bottom: 30px;
  line-height: 1.5;
  width: 100%;
`;

const ModalButton = styled.button`
  width: 100%;
  height: 50px;
  background-color: #0075ff;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  align-self: center;
`;

const Bold = styled.span`
  font-weight: 700;
`;

// 추가 스타일 컴포넌트
const ResendHighlight = styled.span`
  color: #fe7156;
  text-decoration: underline;
  font-weight: 600;
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #333;
  margin-bottom: 15px;
  width: 100%;
`;

// 인증 완료 배지 스타일
const VerifiedBadge = styled.span`
  display: inline-block;
  background-color: #00c853;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  margin-top: 6px;
`;

export default LawyerVerification;
